/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-02-16 19:58:17
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { Component } from 'react';
import { Field } from 'redux-form';
import { Stack, Button, FormLayout, Icon } from '@shopify/polaris';
import { CirclePlusMinor, DeleteMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield-2';
import { RenderDatePicker } from './render-date-picker';
import { RenderSelect2CreatableV2 } from 'src/components/views/common/forms/render-select-2-creatable-v2';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2';
import RenderSelectProduct from 'src/components/containers/merchandising-v2/rule/strategy/attributes/render-select-product';
import { isNumber, isRequired } from 'src/helpers/form-validation-helper';

import {
  NUMBER_ATTRIBUTE_PER_RULE,
  PRODUCT_ATTRIBUTES,
  CONDUCTOR_FOR_ATTRIBUTES,
  CONDUCTOR_FOR_PRODUCT_INVENTORY, CONDUCTOR_IS
} from 'src/constants/merchandising-v2';
import { capitalizeFirstLetter } from 'src/helpers';

type Props = {
  fields: Object
};

type ComposedProps = Props & WithI18nProps;
type State = {
  attributeList: Array<{ value: string, label: string }>,
  allConductorList: Object
};
class AttributesTable extends Component<ComposedProps, State> {
  state = {
    attributeList: [],
    allConductorList: {},
  };

  componentWillUpdate(nextProps, nextState) {
    //Init value when change type strategy
    const { fields } = nextProps;
    if (fields.length <= 0) {
      this.addAttribute();
    }
  }
  componentDidMount() {
    const { i18n, options, appLanguage, fields, ruleType } = this.props;
    let attributeList = _.map(PRODUCT_ATTRIBUTES, attribute => ({
      value: attribute.value,
      label: i18n.translate(attribute.label),
      nonDuplicate: attribute.nonDuplicate
    }));

    // PFSN-43887, separate product_id to a tab, remove product_id from attribute
    _.remove(attributeList, attribute => attribute.value === 'product_id');
    if (ruleType === 'collection') {
      _.remove(attributeList, attribute => attribute.value === 'collection');
    }
    if (options && options.length > 0) {
      options.map(item => {
        if (item) {
          attributeList.push({
            value: `opt_${item.name}`,
            label: i18n.translate('AdminHTML.MerchandisingV2.Constant.productAttributes_Option', {
              nt_option_label: capitalizeFirstLetter(item.originalName, appLanguage)
            })
          });
        }
        return '';
      });
    }

    let allConductorList = {};
    for (const [key, value] of Object.entries(CONDUCTOR_FOR_ATTRIBUTES)) {
      allConductorList[key] = _.map(value, function (item) {
        return {
          ...item,
          label: i18n.translate(item.label)
        };
      });
    }

    if (fields.length <= 0) {
      this.addAttribute();
    }
    this.setState({ attributeList, allConductorList });
  }

  isRequiredValue = isRequired(
    this.props.i18n.translate('AdminHTML.MerchandisingV2.Helper.validateBlankValue')
  );
  render() {
    const { fields, i18n, attributes, type } = this.props;

    const { attributeList } = this.state;
    const selectedAttribute = _.filter(attributes, a => a.attribute).map(a => a.attribute);
    attributeList
      .forEach(attribute => attribute.disabled = attribute.nonDuplicate && selectedAttribute.includes(attribute.value))
    return (
      <div className="BC-MerchandisingV2__Attributes-Table">
        <Stack vertical>
          {fields.map((item, index) => {
            return (
              <div className="BC-MerchandisingV2__Attribute" key={index}>
                <FormLayout>
                  {this.renderSelectAttribute(item, index, fields)}
                </FormLayout>
              </div>
            );
          })}
          {attributes.length < NUMBER_ATTRIBUTE_PER_RULE[type] && (
            <div className="add-field-attribute">
              <p style={{ maxWidth: 'fit-content' }} onClick={() => this.addAttribute()}>
                <Icon source={CirclePlusMinor} color="base" />{' '}
                <span>
                  {' '}
                  {i18n.translate('AdminHTML.MerchandisingV2.AttributesModal.and_Label')}
                </span>
              </p>
            </div>
          )}
        </Stack>
      </div>
    );
  }

  renderSelectAttribute = (item, index, fields) => {
    const { i18n } = this.props;
    const { attributeList } = this.state;

    const select = (
      <div style={{ marginTop: '10px' }}>
        <Field
          name={`${item}].attribute`}
          component={RenderSelect}
          options={attributeList}
          dataType="string"
          onChange={(e, value, oldValue) =>
            this.changeProductAttribute(value, oldValue, index)
          }
          placeholder={i18n.translate(
            'AdminHTML.MerchandisingV2.AttributesModal.field_ProductAttribute_Placeholder'
          )}
        />
        {this.renderField(item, index)}
      </div>
    )
    if (fields.length > 1) {
      return (
        <div style={{ display: 'flex', width: '100%' }}>
          <div style={{ width: '100%' }}>
            {select}
          </div>
          <div className="Polaris-Labelled__LabelWrapper">
            <Button
              icon={DeleteMinor}
              plain
              onClick={() => this.removeAttribute(index)}>
            </Button>
          </div>
        </div>
      )
    }

    return select;
  }

  renderField = (item, index) => {
    const conductor = this.renderConductorField(item, index);
    const field = this.renderValueField(item, index);
    if (!field) {
      return <></>
    }
    return (
      <div className="render-queries-field" key={index}>
        <div className="border-connect-input"></div>
        <div className="wrap-attribute-field">
          <div className="attribute-conductor">
            {conductor}
          </div>
          <div className="attribute-field">
            {field}
          </div>
        </div>
      </div>
    )
  }

  renderConductorField = (item, index) => {
    const { i18n, attributes } = this.props;
    const { allConductorList } = this.state;
    const attribute = _.get(attributes, `[${index}].attribute`, '');

    let conductorList = _.isEmpty(attribute)
      ? []
      : _.includes(attribute, `opt`)
      ? allConductorList['option']
      : allConductorList[attribute];

    let isOptionsIs = false
    if (!conductorList || _.isEmpty(conductorList)) {
      conductorList = CONDUCTOR_IS.map(({ label, value }) => ({ value, label: i18n.translate(label)}));
      isOptionsIs = true;
    }
    return (
      <Field
        name={`${item}.conductor`}
        component={RenderSelect}
        options={conductorList}
        dataType="string"
        placeholder={isOptionsIs ? undefined : i18n.translate(
          'AdminHTML.MerchandisingV2.AttributesModal.field_Validity_Placeholder'
        )}
      />
    );
  };

  renderValueField = (item, index) => {
    const { i18n, attributes, collections } = this.props;
    const valueDate = _.get(attributes, `[${index}].value`, '');
    const attribute = _.get(attributes, `[${index}].attribute`, '');
    const conductor = _.get(attributes, `[${index}].conductor`, '');

    const disabled = _.isEmpty(attribute) || _.isEmpty(conductor);

    const optionsInventory = CONDUCTOR_FOR_PRODUCT_INVENTORY.map(e => ({
      value: e.value,
      label: i18n.translate(e.label)
    }));
    if (_.isEmpty(attribute)) {
      return null;
    }

    // Options
    if (_.includes(attribute, 'opt_')) {
      return (
        <Field
          name={`${item}.value`}
          component={RenderSelect2CreatableV2}
          placeholder={i18n.translate(
            'AdminHTML.MerchandisingV2.AttributesModal.field_Value_Placeholder'
          )}
          type={'options'}
          filterPrefix={this.getFilterPrefix(attribute)}
          isDisabled={disabled}
          validate={[this.isRequiredValue]}
        />
      );
    }

    const textField = (
      <Field
        name={`${item}.value`}
        type="text"
        placeholder={i18n.translate(
          'AdminHTML.MerchandisingV2.AttributesModal.field_Value_Placeholder'
        )}
        component={RenderTextField}
        anotherProps={{ disabled }}
        validate={[this.isRequiredValue]}
      />
    );

    const textFieldPrice = (
      <Field
        name={`${item}.value`}
        type="number"
        min={0}
        placeholder={i18n.translate(
          'AdminHTML.MerchandisingV2.AttributesModal.field_Value_Placeholder'
        )}
        component={RenderTextField}
        validate={[this.isRequiredValue, isNumber]}
        anotherProps={{ disabled }}
      />
    );

    const datePickerField = (
      <Field
        name={`${item}.value`}
        index={index}
        component={RenderDatePicker}
        onChooseDate={this.onChooseDate}
        start={valueDate}
        end={valueDate}
        disabled={disabled}
        validate={[this.isRequiredValue]}
      />
    );
    const selectInventory = (
      <Field
        name={`${item}.value`}
        component={RenderSelect}
        dataType="string"
        options={optionsInventory}
        placeholder={i18n.translate(
          'AdminHTML.MerchandisingV2.AttributesModal.field_Value_Placeholder'
        )}
        disabled={disabled}
        validate={[this.isRequiredValue]}
      />
    );

    const select2CreatableFieldVendor = (
      <Field
        name={`${item}.value`}
        component={RenderSelect2CreatableV2}
        placeholder={i18n.translate(
          'AdminHTML.MerchandisingV2.AttributesModal.field_Value_Placeholder'
        )}
        type={'vendors'}
        filterPrefix={''}
        isDisabled={disabled}
        validate={[this.isRequiredValue]}
      />
    );

    const select2CreatableFieldProductType = (
      <Field
        name={`${item}.value`}
        component={RenderSelect2CreatableV2}
        placeholder={i18n.translate(
          'AdminHTML.MerchandisingV2.AttributesModal.field_Value_Placeholder'
        )}
        type={'product_types'}
        filterPrefix={''}
        isDisabled={disabled}
        validate={[this.isRequiredValue]}
      />
    );

    const select2CreatableFieldProductCategory = (
      <Field
        name={`${item}.value`}
        component={RenderSelect2CreatableV2}
        placeholder={i18n.translate(
          'AdminHTML.MerchandisingV2.AttributesModal.field_Value_Placeholder'
        )}
        type={'product_categories'}
        filterPrefix={''}
        isDisabled={disabled}
        validate={[this.isRequiredValue]}
      />
    );

    const select2CreatableFieldTag = (
      <Field
        name={`${item}.value`}
        component={RenderSelect2CreatableV2}
        placeholder={i18n.translate(
          'AdminHTML.MerchandisingV2.AttributesModal.field_Value_Placeholder'
        )}
        type={'tags'}
        filterPrefix={''}
        isDisabled={disabled}
        validate={[this.isRequiredValue]}
      />
    );

    const selectCollection = (
      <Field
        name={`${item}.value`}
        component={RenderSelect2}
        options={collections}
        placeholder={i18n.translate(
          'AdminHTML.MerchandisingV2.AttributesModal.field_Value_Placeholder'
        )}
        isDisabled={disabled}
        validate={[this.isRequiredValue]}
      />
    );

    const selectProduct = (
      <>
        <Field
          name={`${item}.value`}
          component={RenderSelectProduct}
          isDisabled={disabled}
          validate={[this.isRequiredValue]}
        />
      </>
    );
    const field = {
      title: textField,
      product_type: select2CreatableFieldProductType,
      product_category: select2CreatableFieldProductCategory,
      vendor: select2CreatableFieldVendor,
      price: textFieldPrice,
      inventory: selectInventory,
      tag: select2CreatableFieldTag,
      created_at: datePickerField,
      published_at: datePickerField,
      collection: selectCollection,
      product_id: selectProduct
    };

    return field[attribute];
  };

  getFilterPrefix = attribute => {
    const optionName = attribute ? attribute.replace('opt_', '') : attribute;
    const filterPrefix = `${optionName}:`;

    return filterPrefix;
  };

  // Handle choose date from picker
  onChooseDate = (selectedDates, index) => {
    const { updateField, strategiesIndex } = this.props;
    const { start } = selectedDates;
    // Save date object to field
    updateField(`strategies[${strategiesIndex}].attributes[${index}].value`, start);

    // Render preview
    // renderPreview('attributes-table onChooseDate()');
  };

  // Handle change product attribute
  changeProductAttribute = (newAttribute, oldAttribute, index) => {
    const { updateField, strategiesIndex, type } = this.props;

    if (_.isEmpty(CONDUCTOR_FOR_ATTRIBUTES[newAttribute])) {
      updateField(`strategies[${strategiesIndex}].attributes[${index}].conductor`, 'equal');
    } else {
      updateField(`strategies[${strategiesIndex}].attributes[${index}].conductor`, '');
    }
    updateField(`strategies[${strategiesIndex}].type`, type);
    updateField(`strategies[${strategiesIndex}].attributes[${index}].value`, '');

    // Render preview
    // renderPreview('attributes-table changeProductAttribute()');
  };

  addAttribute = () => {
    const { fields, strategiesIndex, type, updateField } = this.props;
    fields.push({});
    updateField(`strategies[${strategiesIndex}].type`, type);
    // Render preview
    // renderPreview('attributes-table addAttribute()');
  };

  removeAttribute = index => {
    const { fields } = this.props;
    fields.remove(index);

    // Render preview
    // renderPreview('attributes-table removeAttribute()');
  };
}
export default withI18n()(AttributesTable);
